import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  DataTable,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FilterSelect,
  Icons,
  RowAction,
  RowActions,
  Stack,
  StatusChip,
  Tab,
  Tabs,
  TextField,
  Typography,
  statusTone,
  useRowActionsColumn,
} from '@munifocus/ui';
import type { PermitRow } from '@munifocus/contracts-gov';
import { AppShell } from '../../components/AppShell';
import { PageIntro } from '../../components/PageIntro';
import { useScope } from '../../providers/ScopeProvider';
import { formatDate } from '../../lib/relative-time';
import { useMutationToast } from '../../lib/mutation-toast';
import {
  downloadPermit,
  useActiveMunicipality,
  useIssuedPermits,
  useModuleSwitches,
  usePermitApplications,
  useRevokePermit,
} from '../../lib/permits-api';
import { PermitTypesPanel } from './PermitTypesPanel';

const QUEUE_FILTERS = ['open', 'approved', 'refused', 'withdrawn'] as const;
type QueueFilter = (typeof QUEUE_FILTERS)[number] | 'all';

/**
 * Permits & licensing: the application queue, the permits issued, and — for
 * whoever sets them up — the permit types the municipality offers.
 */
export function Permits() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { tab } = useParams<{ tab?: string }>();
  const { can } = useScope();
  const lg = useActiveMunicipality();
  const modules = useModuleSwitches();

  const tabs = [
    { key: 'queue', label: t('gov.permits.tabs.queue') },
    { key: 'issued', label: t('gov.permits.tabs.issued') },
    ...(can('permit:configure') ? [{ key: 'types', label: t('gov.permits.tabs.types') }] : []),
  ];
  const active = tabs.find((x) => x.key === tab)?.key ?? 'queue';

  return (
    <AppShell title={t('gov.permits.title')}>
      <PageIntro>{t('gov.permits.intro')}</PageIntro>
      {!lg ? (
        <Alert severity="info">{t('gov.permits.pickMunicipality')}</Alert>
      ) : !modules.permits ? (
        <Alert severity="info">{t('gov.permits.notEnabled')}</Alert>
      ) : (
        <Stack spacing={2}>
          <Tabs
            value={active}
            onChange={(_, v: string) => navigate(`/permits/${v}`)}
            aria-label={t('gov.permits.title')}
          >
            {tabs.map((x) => (
              <Tab key={x.key} value={x.key} label={x.label} />
            ))}
          </Tabs>
          {active === 'queue' && <ApplicationQueue lg={lg} />}
          {active === 'issued' && <IssuedPermits lg={lg} />}
          {active === 'types' && <PermitTypesPanel lg={lg} />}
        </Stack>
      )}
    </AppShell>
  );
}

function ApplicationQueue({ lg }: { lg: string }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const actionsColumn = useRowActionsColumn();
  const [filter, setFilter] = useState<QueueFilter>('open');
  const applications = usePermitApplications(lg, filter === 'all' ? '' : filter);
  const fr = i18n.language.startsWith('fr');

  return (
    <DataTable
      rows={applications.data ?? []}
      getRowKey={(r) => r.id}
      isLoading={applications.isLoading}
      emptyLabel={t('gov.permits.queue.empty')}
      pageSize={25}
      initialSortKey="submitted"
      initialSortDir="desc"
      onRowClick={(r) => navigate(`/permits/applications/${r.id}`)}
      search={{
        placeholder: t('gov.permits.queue.search'),
        of: (r) =>
          `${r.reference} ${r.applicantName} ${r.organizationName ?? ''} ${r.propertyAddress}`,
      }}
      filters={
        <FilterSelect<QueueFilter>
          label={t('gov.permits.queue.show')}
          value={filter}
          onChange={setFilter}
          allLabel={t('gov.permits.queue.filters.all')}
          options={QUEUE_FILTERS.map((f) => ({
            value: f,
            label: t(`gov.permits.queue.filters.${f}`),
          }))}
          wide
        />
      }
      columns={[
        {
          key: 'applicant',
          label: t('gov.permits.fields.applicant'),
          primary: true,
          sortValue: (r) => r.applicantName,
          cell: (r) => (
            <Stack>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {r.organizationName ?? r.applicantName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {r.reference} · {r.propertyAddress}
              </Typography>
            </Stack>
          ),
        },
        {
          key: 'type',
          label: t('gov.permits.fields.type'),
          sortValue: (r) => (fr ? r.typeNameFr : r.typeNameEn),
          cell: (r) => (fr ? r.typeNameFr : r.typeNameEn),
        },
        {
          key: 'status',
          label: t('gov.permits.fields.status'),
          sortValue: (r) => r.status,
          cell: (r) => (
            <StatusChip
              status={statusTone.permitApplication[r.status]}
              label={t(`gov.permits.statuses.${r.status}`)}
            />
          ),
        },
        {
          key: 'fee',
          label: t('gov.permits.fields.fee'),
          optional: true,
          sortValue: (r) => r.feeStatus,
          cell: (r) => (
            <StatusChip
              status={statusTone.permitFee[r.feeStatus]}
              label={t(`gov.permits.feeStatuses.${r.feeStatus}`)}
            />
          ),
        },
        {
          key: 'submitted',
          label: t('gov.permits.fields.submitted'),
          nowrap: true,
          sortValue: (r) => r.submittedAt ?? '',
          cell: (r) => formatDate(r.submittedAt, i18n.language),
        },
        {
          ...actionsColumn,
          cell: (r) => (
            <RowActions>
              <RowAction
                label={t('gov.permits.queue.open')}
                icon={Icons.ChevronRight}
                onClick={() => navigate(`/permits/applications/${r.id}`)}
              />
            </RowActions>
          ),
        },
      ]}
    />
  );
}

function IssuedPermits({ lg }: { lg: string }) {
  const { t, i18n } = useTranslation();
  const toast = useMutationToast();
  const { can } = useScope();
  const actionsColumn = useRowActionsColumn();
  const permits = useIssuedPermits(lg);
  const revoke = useRevokePermit(lg);
  const [revoking, setRevoking] = useState<PermitRow | null>(null);
  const [reason, setReason] = useState('');
  const fr = i18n.language.startsWith('fr');

  const download = async (p: PermitRow, locale: 'en' | 'fr') => {
    try {
      await downloadPermit(lg, p.id, p.number, locale);
    } catch (err) {
      toast.error(err, 'gov.permits.errors.download');
    }
  };

  return (
    <>
      <DataTable
        rows={permits.data ?? []}
        getRowKey={(r) => r.id}
        isLoading={permits.isLoading}
        emptyLabel={t('gov.permits.issued.empty')}
        pageSize={25}
        initialSortKey="issued"
        initialSortDir="desc"
        search={{
          placeholder: t('gov.permits.issued.search'),
          of: (r) => `${r.number} ${r.holderName} ${r.propertyAddress}`,
        }}
        columns={[
          {
            key: 'number',
            label: t('gov.permits.fields.number'),
            primary: true,
            mono: true,
            sortValue: (r) => r.number,
            cell: (r) => r.number,
          },
          {
            key: 'holder',
            label: t('gov.permits.fields.holder'),
            sortValue: (r) => r.holderName,
            cell: (r) => (
              <Stack>
                <Typography variant="body2">{r.holderName}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {fr ? r.typeNameFr : r.typeNameEn} · {r.propertyAddress}
                </Typography>
              </Stack>
            ),
          },
          {
            key: 'status',
            label: t('gov.permits.fields.status'),
            sortValue: (r) => r.status,
            cell: (r) => (
              <StatusChip
                status={statusTone.permit[r.status]}
                label={t(`gov.permits.permitStatuses.${r.status}`)}
              />
            ),
          },
          {
            key: 'issued',
            label: t('gov.permits.fields.issued'),
            nowrap: true,
            sortValue: (r) => r.issuedAt,
            cell: (r) => formatDate(r.issuedAt, i18n.language),
          },
          {
            key: 'expires',
            label: t('gov.permits.fields.expires'),
            nowrap: true,
            sortValue: (r) => r.expiresAt ?? '9999',
            cell: (r) =>
              r.expiresAt ? formatDate(r.expiresAt, i18n.language) : t('gov.permits.noExpiry'),
          },
          {
            ...actionsColumn,
            cell: (r) => (
              <RowActions>
                <RowAction
                  label={t('gov.permits.downloadEn')}
                  icon={Icons.Download}
                  onClick={() => void download(r, 'en')}
                />
                <RowAction
                  label={t('gov.permits.downloadFr')}
                  icon={Icons.Download}
                  onClick={() => void download(r, 'fr')}
                />
                {can('permit:review') && r.status === 'active' && (
                  <RowAction
                    label={t('gov.permits.revoke.action')}
                    icon={Icons.Ban}
                    onClick={() => {
                      setReason('');
                      setRevoking(r);
                    }}
                  />
                )}
              </RowActions>
            ),
          },
        ]}
      />
      <Dialog open={Boolean(revoking)} onClose={() => setRevoking(null)} fullWidth maxWidth="sm">
        <DialogTitle>{t('gov.permits.revoke.title', { number: revoking?.number })}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t('gov.permits.revoke.body')}
            </Typography>
            <TextField
              label={t('gov.permits.revoke.reason')}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              multiline
              minRows={3}
              fullWidth
              required
              helperText={t('common.validation.minLength', { count: 5 })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevoking(null)}>{t('common.actions.cancel')}</Button>
          <Button
            color="error"
            variant="contained"
            disabled={revoke.isPending || reason.trim().length < 5}
            onClick={async () => {
              if (!revoking) return;
              try {
                await revoke.mutateAsync({ id: revoking.id, reason: reason.trim() });
                toast.success('gov.permits.revoke.done');
                setRevoking(null);
              } catch (err) {
                toast.error(err, 'gov.admin.errors.actionFailed');
              }
            }}
          >
            {t('gov.permits.revoke.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
